import React, { ReactElement, useState } from "react";
import { IDimensions } from "./chartUtils";
import * as d3 from "d3";
import { useTheme, Tooltip } from "@mui/material";
import { IScaleViewPort, ISeriesWithAxes, TickTypes } from "./types";

interface IAxisVerticalProps {
  chartID: string;
  dimensions: IDimensions;
  label?: string;
  formatTick?: (n: number | { value: number, }, index: number, length: number) => string;
  ticks: TickTypes;
  scale: d3.ScaleLinear<number, number> | d3.ScaleBand<string>;
  side?: "LEFT" | "RIGHT";
  index?: number;
  series?: ISeriesWithAxes[];
  allowPan?: boolean;
  updateScaleDomain?: (viewPort: IScaleViewPort) => void;
  stacked?: boolean;
  color?: string;
}

interface IViewPortEdition {
  viewPort: IScaleViewPort;
  enabled: boolean;
  action: "MOVE" | "SCALE";
  init: number;
  factor: number;
  time: number;
}

const xTickMargin = 5;
const axisWidth = 70;
const delta250ms = 250;

const getDominantBaseline = (index: number, max: number): "auto" | "hanging" | "middle" => {
  if (index === 0) {
    return "auto";
  }

  return index === max - 1 ? "hanging" : "middle";
};

const AxisVertical = ({ chartID, dimensions, label, formatTick, ticks, scale, side, index, series, stacked, updateScaleDomain, color }: IAxisVerticalProps): ReactElement => {
  const [ viewPortEdition, setViewPortEdition ] = useState<IViewPortEdition>({ viewPort: { scale: 1, offset: 0, action: "RESET" }, enabled: false, action: "MOVE", init: 0, factor: 0, time: 0 });
  const axisIndex =  index ?? 0;
  const theme = useTheme();
  const verticalID = `${chartID}-axis-vertical-${axisIndex}`;

  const isRight = !side || side === "RIGHT";
  const margin = stacked ?  0 : axisIndex * axisWidth;
  const xTickPosition = isRight ? xTickMargin: -xTickMargin;
  const offset = stacked ? axisIndex * dimensions.boundedHeight + axisIndex * 15 : 0;

  let yPos = dimensions.boundedHeight - 5;

  const onMouseDown = (e: React.MouseEvent<SVGRectElement>) => {
    if (updateScaleDomain){
      const domain = scale.domain();
      if (domain.length === 2 && !Number.isNaN(domain[0]) && !Number.isNaN(domain[1])) {
        const min = domain[0] as number;
        const max = domain[1] as number;
        const factor = (max - min) / dimensions.boundedHeight;

        setViewPortEdition({ ...viewPortEdition, enabled: true, init: e.clientY ?? 0, factor });
      }
    }
  };

  const onMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
    if (updateScaleDomain && viewPortEdition.enabled) {
      const newViewPort: IScaleViewPort = e.altKey
        ? {
          ...viewPortEdition.viewPort,
          scale: (viewPortEdition.init - e.clientY) * viewPortEdition.factor,
          action: "SCALE"
        }
        : {
          ...viewPortEdition.viewPort,
          offset: (e.clientY - viewPortEdition.init) * viewPortEdition.factor,
          action: "OFFSET"
        };

      updateScaleDomain(newViewPort);
      setViewPortEdition({ ...viewPortEdition, viewPort: newViewPort,  init: e.clientY ?? 0,  });
    }
  };

  const onMouseUp = () => {
    if (updateScaleDomain && viewPortEdition.enabled) {
      if (Date.now() - viewPortEdition.time < delta250ms) {
        setViewPortEdition({ ...viewPortEdition, enabled: false, time: Date.now(), viewPort: { scale: 1, offset: 0, action: "RESET" } });
        updateScaleDomain({ scale: 1, offset: 0, action: "RESET" });
      } else {
        setViewPortEdition({ ...viewPortEdition, enabled: false, time: Date.now() });
      }
    }
  };

  return (
    <g className="Axis AxisHorizontal" transform={`translate(${isRight ?  dimensions.boundedWidth : 0}, ${offset})`}>
      <line className="Axis__line" x1={isRight ? margin : - margin} x2={isRight ? margin : - margin} y2={dimensions.boundedHeight} />

      {ticks.map((tick: any, i) =>
        <text
          key={`${verticalID}-tick-${i}`}
          fill={ color ?? theme.palette.text.primary}
          x={Math.round((isRight ? margin : -margin) + xTickPosition)}
          y={Math.round(scale(tick) ?? 0)}
          textAnchor={isRight ? "start" : "end"}
          dominantBaseline={getDominantBaseline(i, ticks.length)} >
          {formatTick && typeof tick === "number" ? formatTick(tick, i, ticks.length) : tick}
        </text>
      )}

      {label &&
      <text className="Axis__label"
        transform={`translate(${isRight ? margin + 55 : -margin - 40}, ${dimensions.boundedHeight / 2}) rotate(-90)`}
        textAnchor="middle"
        fill={ color ?? theme.palette.text.primary}
        fontWeight="bold" fontSize="1em">
        {label}
      </text>
      }

      {series && series.map((s, si) => {
        const i = si % 2;
        const x = margin + i * 14 + 10;

        if(i === 0){
          yPos += 14;
        }

        return(
          <Tooltip key={`${verticalID}-series-${si}`} title={s.name} >
            <circle cx={x} cy={yPos} r={3} fill={s.color} />
          </Tooltip>
        );
      })
      }

      <rect fill="#000" fillOpacity={.01} x={margin} width={axisWidth} height={dimensions.boundedHeight}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}/>
    </g>
  );
};

export default AxisVertical;
